import {
    ComponentType,
    MouseEvent,
    ReactNode,
    Ref,
    useCallback,
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    KeyboardEvent,
} from 'react';
import {
    addEventListenerOnTransitionEnded,
    ANIMATION_DURATION,
    ElementOrElementGetter,
    handleRefSplit,
    PositionStrict,
    removeEventListenerOnTransitionEnded,
} from '@optimus-web/common';
import { Positioner, PositioningStrategy, useId } from '@optimus-web/helpers';
import { UnicodeEntity } from '@mews/enums';
import { VisuallyHidden } from '../helpers/VisuallyHidden';
import { getTooltipPositionFromPlacement } from './helpers';
import { Arrow, ArrowPath, TooltipContentElement, TooltipElement } from './style';

const TOOLTIP_MAX_WIDTH = 300;

export interface TooltipProps {
    tooltip?: ReactNode;
    tooltipDelay?: number;
    tooltipHideOnClick?: boolean;
    tooltipShowOnClick?: boolean;
    tooltipPosition?: PositionStrict;
    anchorElementOverride?: ElementOrElementGetter;
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    onMouseEnter?: (e: MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (e: MouseEvent<HTMLElement>) => void;
    maxWidth?: number;
    offset?: number;
    openOnMount?: boolean;
    inline?: boolean;
    tooltipRef?: Ref<Positioner>;
    inPortal?: boolean;
    positioningStrategy?: PositioningStrategy;
    children?: ReactNode;
    enableStaticAccessibility?: boolean;
}

export interface TooltipImperativeHandle {
    activate: () => void;
    deactivate: () => void;
}

export const Tooltip = <P extends {}>(ComposedComponent: ComponentType<P>) => {
    const TooltipComponent = forwardRef<TooltipImperativeHandle, P & TooltipProps>((props, ref) => {
        const {
            tooltip,
            tooltipDelay = 0,
            tooltipHideOnClick = true,
            tooltipShowOnClick = false,
            openOnMount = false,
            tooltipPosition,
            anchorElementOverride,
            onClick,
            onMouseEnter,
            onMouseLeave,
            maxWidth,
            offset,
            inline,
            tooltipRef,
            inPortal,
            positioningStrategy,
            children,
            enableStaticAccessibility = false,
            ...other
        } = props;

        const [active, setActive] = useState(false);
        const [visible, setVisible] = useState(false);

        const tooltipElementRef = useRef<HTMLDivElement | null>(null);
        const anchorElementRef = useRef<HTMLDivElement | null>(null);
        const positionerRef = useRef<Positioner | null>(null);
        const prevActiveRef = useRef<boolean>(active);
        const timeoutId = useRef<number | null>(null);
        const tooltipId = useId('tooltip');

        const clearTimeoutIfExists = useCallback(() => {
            if (timeoutId.current) {
                window.clearTimeout(timeoutId.current);
                timeoutId.current = null;
            }
        }, []);

        const handleDeactivationEnd = useCallback(() => {
            setVisible(false);
        }, []);

        const activate = useCallback(() => {
            if (!tooltip) {
                return;
            }

            clearTimeoutIfExists();

            setVisible(true);

            timeoutId.current = window.setTimeout(() => {
                setActive(true);
            }, tooltipDelay);
        }, [tooltip, tooltipDelay, clearTimeoutIfExists]);

        const deactivate = useCallback(() => {
            clearTimeoutIfExists();

            setActive(false);

            timeoutId.current = window.setTimeout(() => {
                setVisible(false);
            }, ANIMATION_DURATION.fast);
        }, [clearTimeoutIfExists]);

        /**
         * Expose activate and deactivate methods through imperative handle.
         * This allows external components (like backend renderers) to programmatically control tooltip visibility.
         */
        useImperativeHandle(
            ref,
            () => ({
                activate,
                deactivate,
            }),
            [activate, deactivate]
        );

        const handleClick = useCallback(
            (e: MouseEvent<HTMLElement>) => {
                if (tooltipHideOnClick && active) {
                    deactivate();
                }

                if (tooltipShowOnClick && !active) {
                    activate();
                }

                if (onClick) {
                    onClick(e);
                }
            },
            [active, deactivate, activate, onClick, tooltipHideOnClick, tooltipShowOnClick]
        );

        const handleMouseEnter = useCallback(
            (e: MouseEvent<HTMLElement>) => {
                activate();

                if (onMouseEnter) {
                    onMouseEnter(e);
                }
            },
            [activate, onMouseEnter]
        );

        const handleMouseLeave = useCallback(
            (e: MouseEvent<HTMLElement>) => {
                if (!openOnMount) {
                    deactivate();
                }

                if (onMouseLeave) {
                    onMouseLeave(e);
                }
            },
            [deactivate, onMouseLeave, openOnMount]
        );
        const handleFocus = useCallback(() => {
            activate();
        }, [activate]);

        const handleBlur = useCallback(() => {
            if (!openOnMount) {
                deactivate();
            }
        }, [deactivate, openOnMount]);

        const handleKeyDown = useCallback(
            (e: KeyboardEvent<HTMLElement>) => {
                if (e.key === 'Escape' && active) {
                    e.preventDefault();
                    deactivate();
                }
            },
            [active, deactivate]
        );

        useEffect(() => {
            if (openOnMount) {
                activate();
            }
        }, [openOnMount, activate]);

        useEffect(
            () => () => {
                clearTimeoutIfExists();
                removeEventListenerOnTransitionEnded(tooltipElementRef.current, handleDeactivationEnd);
            },
            [clearTimeoutIfExists, handleDeactivationEnd]
        );

        useEffect(() => {
            const tooltipElement = tooltipElementRef.current;

            if (!tooltipElement) return;

            const prevActive = prevActiveRef.current;

            if (prevActive && !active) {
                addEventListenerOnTransitionEnded(tooltipElement, handleDeactivationEnd);
            }

            if (!prevActive && active) {
                removeEventListenerOnTransitionEnded(tooltipElement, handleDeactivationEnd);
            }

            prevActiveRef.current = active;
        }, [active, handleDeactivationEnd]);

        const componentProps = {
            ...other,
            ref: anchorElementRef,
            onClick: handleClick,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onFocus: handleFocus,
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            /**
             * We need to set the tabIndex to 0 and the role to img if the element is static.
             * Static elements with no role will trigger an accessibility error.
             * img role seems to be the most appropriate for this use case.
             * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/img_role
             */
            ...(enableStaticAccessibility && { tabIndex: 0, role: 'img' }),
            'aria-describedby': tooltip && !enableStaticAccessibility ? `${tooltipId}-sr-only-description` : undefined,
            'aria-labelledby': enableStaticAccessibility ? `${tooltipId}-sr-only-label` : undefined,
        } as unknown as P;

        const position = positionerRef.current
            ? getTooltipPositionFromPlacement(positionerRef.current.getCurrentPlacement())
            : tooltipPosition;

        const tooltipProps = {
            active,
            position,
            ref: tooltipElementRef,
            id: tooltipId,
            role: 'tooltip',
            'aria-hidden': enableStaticAccessibility,
        };

        const positionerProps = {
            anchorElement: anchorElementOverride || anchorElementRef.current,
            opened: visible,
            maxWidth: maxWidth || TOOLTIP_MAX_WIDTH,
            ref: handleRefSplit({ ownRef: positionerRef, forwardedRef: tooltipRef }),
            disablePointerEvents: true,
            placement: tooltipPosition,
            offset: offset ?? 8,
            inPortal,
            positioningStrategy,
        };

        return (
            <>
                <ComposedComponent {...componentProps}>{children}</ComposedComponent>
                {visible && (
                    <Positioner {...positionerProps}>
                        <TooltipElement {...tooltipProps}>
                            <TooltipContentElement inline={inline}>{tooltip}</TooltipContentElement>
                            <Arrow position={position} data-popper-arrow>
                                <svg
                                    width="9"
                                    height="4"
                                    viewBox="0 0 9 4"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <ArrowPath
                                        d="M9 0L5.16436 3.40945C4.78548 3.74624 4.21452 3.74624 3.83564 3.40945L0 0H9Z"
                                        fill="none"
                                    />
                                </svg>
                            </Arrow>
                        </TooltipElement>
                    </Positioner>
                )}

                {/*
                 * We need to build a custom label if the element is static.
                 * This elements do not work with the aria-describedby attribute.
                 * We hide children and tooltip contents from screen readers.
                 */}
                {enableStaticAccessibility ? (
                    <VisuallyHidden aria-hidden id={`${tooltipId}-sr-only-label`}>
                        {children}
                        {UnicodeEntity.Space}
                        {tooltip}
                    </VisuallyHidden>
                ) : (
                    <VisuallyHidden aria-hidden id={`${tooltipId}-sr-only-description`}>
                        {tooltip}
                    </VisuallyHidden>
                )}
            </>
        );
    });

    return TooltipComponent;
};
